// ============================================================================
// Safe Care - Paramètres généraux des notifications SMS
// Module : features/assistance_sociale/pages
// Route  : /assurance/send-message
// Charte : identique à l'onglet « Configuration Produit Safe care » de
//          /assurance/rupture — barre d'actions .bG + sections numérotées
//          en cartes (badge numéroté + caption + champs inline).
// ============================================================================

import { useEffect, useState } from 'react';
import { Form, Space, Button, Input, InputNumber, Switch, Tabs, Table, Tooltip, Select } from 'antd';
import { Box, Typography, Dialog, DialogTitle, DialogContent, Stack } from '@mui/material';
import { UserOutlined, FileTextOutlined, ContainerFilled, EditOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import FormatSMS from '../../../Models/FormatSMS';
import InputForm from '../../../Components/InputT';
import { useGetParamNotif, useSaveParamNotif, useGetGabaritsSms, useSaveGabaritSms } from '../hooks';
import { ParamNotifSafeCare } from '../types';

// Gabarits SMS Safe Care déployés (scripts 27-28 — modifiables dans FormatSms)
const GABARITS = [
  { codSms: 'SFC01', usage: 'Rappel : cotisation due aujourd\'hui', declencheur: 'Échéance = date banque (si rappel activé)' },
  { codSms: 'SFC02', usage: 'Avertissement impayés', declencheur: 'nb impayés >= seuil avertissement (+ relance tous les X jours si configuré)' },
  { codSms: 'SFC03', usage: 'Message avant suspension', declencheur: 'nb impayés = seuil suspension − 1' },
  { codSms: 'SFC04', usage: 'Suspension automatique', declencheur: 'nb impayés >= seuil suspension' },
  { codSms: 'SFC05', usage: 'Résiliation', declencheur: 'nb impayés >= seuil résiliation' },
  { codSms: 'SFC06', usage: 'Confirmation de prélèvement', declencheur: 'Cotisation prélevée avec succès (si activé)' },
  { codSms: 'SFC07', usage: 'Message avant résiliation', declencheur: 'nb impayés = seuil résiliation − 1 (si activé)' },
  { codSms: 'SFC08', usage: 'Suspension validée par l\'agence', declencheur: 'Validation d\'une demande de suspension (si activé)' },
  { codSms: 'SFC09', usage: 'Réactivation validée', declencheur: 'Validation d\'une demande de réactivation (si activé)' },
  { codSms: 'SFC10', usage: 'Indemnisation payée', declencheur: 'Validation d\'une demande d\'assistance (si activé)' },
  { codSms: 'SFC11', usage: 'Souscription validée (bienvenue)', declencheur: 'Validation d\'une demande de souscription (si activé)' },
  { codSms: 'SFC12', usage: 'Rupture automatique détectée', declencheur: 'nb impayés >= NbImpayesRupture de la version (si activé)' },
  { codSms: 'SFC13', usage: 'Rappel avant échéance', declencheur: 'Échéance = date banque + X jours (si champ renseigné)' },
  { codSms: 'SFC14', usage: 'Délai de grâce avant suspension', declencheur: 'Seuil suspension atteint — délai de régularisation en cours' },
  { codSms: 'SFC15', usage: 'Délai de grâce avant résiliation', declencheur: 'Seuil résiliation atteint — délai de régularisation en cours' },
];

// Puce de code gabarit : au survol, une infobulle explique le code
// (usage + déclencheur) au lieu de l'ancien tableau des gabarits.
// onClick (optionnel) : ouvre le formulaire d'édition du gabarit.
const GabaritTag = ({ code, onClick }: { code: string; onClick?: (code: string) => void }) => {
  const g = GABARITS.find((x) => x.codSms === code);
  return (
    <Tooltip
      placement="left"
      color="#0f172a"
      mouseEnterDelay={0.1}
      title={g ? (
        <div style={{ maxWidth: 280 }}>
          <b>{g.codSms} — {g.usage}</b>
          <div style={{ marginTop: 4 }}>Déclencheur : {g.declencheur}</div>
          {onClick && <div style={{ marginTop: 4, fontStyle: 'italic' }}>Cliquez pour modifier le message.</div>}
        </div>
      ) : code}
    >
      <Box
        onClick={() => onClick?.(code)}
        sx={{
          px: 1, py: 0.25, borderRadius: 1.5, flexShrink: 0,
          cursor: onClick ? 'pointer' : 'help',
          background: 'var(--lightColor002)', display: 'inline-block',
          fontSize: 11, fontWeight: 700, color: 'var(--darkColor)',
        }}
      >{code}</Box>
    </Tooltip>
  );
};

// Événements manuels/transactionnels pilotés par un interrupteur
const EVENEMENTS = [
  { name: 'NotifPrelevementOk', label: 'SMS de confirmation quand une cotisation est prélevée', code: 'SFC06' },
  { name: 'NotifPreResiliation', label: 'SMS au dernier impayé toléré avant résiliation (seuil − 1)', code: 'SFC07' },
  { name: 'NotifSuspensionManuelle', label: 'SMS quand l\'agence valide une demande de suspension', code: 'SFC08' },
  { name: 'NotifReactivation', label: 'SMS quand l\'agence valide une demande de réactivation', code: 'SFC09' },
  { name: 'NotifIndemnisation', label: 'SMS quand une demande d\'assistance est validée et payée', code: 'SFC10' },
  { name: 'NotifSouscription', label: 'SMS de bienvenue quand une souscription est validée', code: 'SFC11' },
  { name: 'NotifRuptureAuto', label: 'SMS quand la souscription entre en rupture automatique', code: 'SFC12' },
];

// ============================================================================
// Carte de section numérotée — identique aux blocs de « Configuration Produit
// Safe care » (badge numéroté + titre + caption + contenu)
// ============================================================================
const SectionCard = ({ numero, titre, caption, children }: {
  numero: number; titre: string; caption?: React.ReactNode; children: React.ReactNode;
}) => (
  <Box sx={{
    border: '1px solid var(--colorOpaque030)',
    borderRadius: 3, p: 2, mb: 2,
    background: '#fff',
    boxShadow: '0 1px 8px rgba(13,74,112, 0.06)',
  }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
      <Box sx={{
        width: 28, height: 28, borderRadius: 1.5,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--lightColor002)', color: 'var(--darkColor)',
        fontSize: 14, fontWeight: 700,
      }}>{numero}</Box>
      <Typography sx={{ fontWeight: 600, color: 'var(--darkblue)', fontSize: 14 }}>
        {titre}
      </Typography>
    </Box>
    {caption && (
      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 2 }}>
        {caption}
      </Typography>
    )}
    {children}
  </Box>
);

const SendMessagePage = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const [form] = Form.useForm();
  const { data: paramRes, isLoading, refetch } = useGetParamNotif();
  const saveParam = useSaveParamNotif();
  const [saving, setSaving] = useState(false);
  const notifActive = Form.useWatch('NotifActive', form);

  // Picklist des événements : la section 4 n'affiche que les événements
  // ACTIVÉS ; la modale ne propose que ceux qui restent disponibles.
  // (état dédié : Form.useWatch ne reflète pas toujours setFieldsValue)
  const [picklistEvtOpen, setPicklistEvtOpen] = useState(false);
  const [picklistEvtSearch, setPicklistEvtSearch] = useState('');
  const [evtActifs, setEvtActifs] = useState<string[]>([]);
  const evtDispos = EVENEMENTS.filter((e) => !evtActifs.includes(e.name));
  const activerEvt = (name: string) => {
    form.setFieldsValue({ [name]: true });
    setEvtActifs((prev) => (prev.includes(name) ? prev : [...prev, name]));
  };
  const retirerEvt = (name: string) => {
    form.setFieldsValue({ [name]: false });
    setEvtActifs((prev) => prev.filter((n) => n !== name));
  };

  // Gabarits SMS (table FormatSms) : ajout / modification d'un code + message.
  // Formulaire en modale — instance de Form dédiée pour ne pas mélanger les
  // champs avec le formulaire principal des paramètres.
  const [gabaritForm] = Form.useForm();
  const [gabaritModalOpen, setGabaritModalOpen] = useState(false);
  const [gabaritSel, setGabaritSel] = useState<FormatSMS | null>(null); // null = ajout
  const [savingGabarit, setSavingGabarit] = useState(false);
  const { data: gabarits } = useGetGabaritsSms();
  const saveGabarit = useSaveGabaritSms();
  // On limite la liste aux gabarits Safe Care (codes SFCxx / type SAFECARE)
  const gabaritsSfc: FormatSMS[] = (gabarits ?? []).filter(
    (g) => (g.codSmS ?? '').toUpperCase().startsWith('SFC') || (g.typSMS ?? '').toUpperCase().includes('SAFECARE')
  );

  const chargerGabarit = (g: FormatSMS) => {
    setGabaritSel(g);
    gabaritForm.setFieldsValue({
      codSmS: g.codSmS,
      descriptionSMS: g.descriptionSMS,
      message: g.message,
      isActif: g.isActif ?? true,
    });
  };

  const nouveauGabarit = () => {
    setGabaritSel(null);
    gabaritForm.resetFields();
    gabaritForm.setFieldsValue({ isActif: true });
  };

  // code = puce SFCxx cliquée : pré-remplit en modification si le gabarit
  // existe déjà, sinon en ajout avec le code pré-rempli.
  const ouvrirModalGabarit = (code?: string) => {
    const g = code ? gabaritsSfc.find((x) => x.codSmS === code) : undefined;
    setGabaritSel(g ?? null);
    gabaritForm.resetFields();
    if (g) {
      gabaritForm.setFieldsValue({
        codSmS: g.codSmS,
        descriptionSMS: g.descriptionSMS,
        message: g.message,
        isActif: g.isActif ?? true,
      });
    } else {
      gabaritForm.setFieldsValue({ codSmS: code ?? '', isActif: true });
    }
    setGabaritModalOpen(true);
  };

  const enregistrerGabarit = () => {
    gabaritForm.validateFields().then((v) => {
      const payload: FormatSMS = {
        ...(gabaritSel ?? {}),
        codSmS: String(v.codSmS).trim().toUpperCase(),
        descriptionSMS: v.descriptionSMS,
        typSMS: gabaritSel?.typSMS ?? 'SAFECARE',
        message: v.message,
        isActif: !!v.isActif,
        codOpe: gabaritSel?.codOpe ?? 'SFC',
        utiSai: user.coduti ?? 'SYSTEME',
        utiModif: user.coduti ?? 'SYSTEME',
      };
      setSavingGabarit(true);
      saveGabarit.mutate({ isNew: !gabaritSel, gabarit: payload }, {
        onSuccess: (res) => {
          setSavingGabarit(false);
          if (res?.StatutCode === 200) {
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success', timer: 1500 });
            setGabaritModalOpen(false);
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: (err: any) => {
          setSavingGabarit(false);
          SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
        },
      });
    }).catch(() => { /* validation du formulaire en échec */ });
  };

  useEffect(() => {
    const row = paramRes?.Result?.Table?.[0] ?? paramRes?.Result?.Table1?.[0];
    if (paramRes?.StatutCode === 200 && row) {
      form.setFieldsValue({
        IdParam: row.IdParam,
        NotifActive: !!row.NotifActive,
        RappelEcheanceJour: !!row.RappelEcheanceJour,
        NbAvertissement: row.NbAvertissement,
        NbSuspension: row.NbSuspension,
        NbResiliation: row.NbResiliation,
        NotifPrelevementOk: !!row.NotifPrelevementOk,
        NotifPreResiliation: !!row.NotifPreResiliation,
        NotifSuspensionManuelle: !!row.NotifSuspensionManuelle,
        NotifReactivation: !!row.NotifReactivation,
        NotifIndemnisation: !!row.NotifIndemnisation,
        NotifSouscription: !!row.NotifSouscription,
        NotifRuptureAuto: !!row.NotifRuptureAuto,
        RelanceImpayeJours: row.RelanceImpayeJours,
        DelaiRegularisationJours: row.DelaiRegularisationJours,
        RappelEcheanceAvantJours: row.RappelEcheanceAvantJours,
      });
      setEvtActifs(EVENEMENTS.filter((e) => !!(row as any)[e.name]).map((e) => e.name));
    }
  }, [paramRes, form]);

  const enregistrer = () => {
    const v = form.getFieldsValue();
    const param: ParamNotifSafeCare = {
      NotifActive: !!v.NotifActive,
      RappelEcheanceJour: !!v.RappelEcheanceJour,
      NbAvertissement: v.NbAvertissement ?? null,
      NbSuspension: v.NbSuspension ?? null,
      NbResiliation: v.NbResiliation ?? null,
      NotifPrelevementOk: !!v.NotifPrelevementOk,
      NotifPreResiliation: !!v.NotifPreResiliation,
      NotifSuspensionManuelle: !!v.NotifSuspensionManuelle,
      NotifReactivation: !!v.NotifReactivation,
      NotifIndemnisation: !!v.NotifIndemnisation,
      NotifSouscription: !!v.NotifSouscription,
      NotifRuptureAuto: !!v.NotifRuptureAuto,
      RelanceImpayeJours: v.RelanceImpayeJours ?? null,
      RappelEcheanceAvantJours: v.RappelEcheanceAvantJours ?? null,
      DelaiRegularisationJours: v.DelaiRegularisationJours ?? null,
      UtiSai: user.coduti ?? 'SYSTEME',
    };
    setSaving(true);
    saveParam.mutate(param, {
      onSuccess: (res) => {
        setSaving(false);
        if (res?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success', timer: 1500 });
        } else {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      },
      onError: (err: any) => {
        setSaving(false);
        SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
      },
    });
  };

  const annuler = () => {
    form.resetFields();
    refetch();
  };

  const items = [{
    key: '1',
    label: <span id="Tab">Paramètres généraux SMS</span>,
    children: (
      <div className="tabItem" style={{ height: '780px', display: 'block' }}>
        <div style={{ width: '85%', height: '100%', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8 }}>
          <Box fontStyle="inherit" className="border border-left" sx={{ p: 1, mb: 2 }}>
            <legend>Paramètres des notifications SMS</legend>
            <Box display="flex" fontStyle="inherit" sx={{ mb: 1, flexWrap: 'wrap', gap: 2 }}>

              {/* Colonne gauche : 1 Activation, 2 Seuils, 3 Relances */}
              <Box fontStyle="inherit" sx={{ flex: 1, minWidth: 380 }}>

                <SectionCard
                  numero={1}
                  titre="Activation des notifications"
                  caption="Interrupteur général et rappels liés aux échéances de cotisation."
                >
                  <Form.Item
                    name="NotifActive" valuePropName="checked" labelCol={{ span: 16 }}
                    label={<b>Envoyer des SMS aux clients</b>}
                  >
                    <Switch />
                  </Form.Item>
                  <Form.Item
                    name="RappelEcheanceJour" valuePropName="checked" labelCol={{ span: 16 }}
                    label={<>Avertir le client le jour où sa cotisation est due <GabaritTag code="SFC01" onClick={ouvrirModalGabarit} /></>}
                  >
                    <Switch disabled={!notifActive} />
                  </Form.Item>
                  <Form.Item
                    name="RappelEcheanceAvantJours" labelCol={{ span: 16 }}
                    label={<>Rappel X jours avant l'échéance <GabaritTag code="SFC13" onClick={ouvrirModalGabarit} /> (vide = jamais)</>}
                  >
                    <InputNumber min={1} max={60} placeholder="Ex: 3" disabled={!notifActive} style={{ width: '100%' }} />
                  </Form.Item>
                  {!notifActive && (
                    <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                      Aucun SMS ne sera envoyé tant que l'interrupteur général est désactivé.
                    </div>
                  )}
                </SectionCard>

                <SectionCard
                  numero={2}
                  titre="Seuils d'alerte par défaut (nombre d'échéances impayées)"
                  caption="Ces seuils s'appliquent uniquement si la version n'a pas ses propres règles (onglet « Interruption & Rupture » de la configuration de version — prioritaire)."
                >
                  <Form.Item name="NbAvertissement" labelCol={{ span: 16 }} label={<>Avertissement client <GabaritTag code="SFC02" onClick={ouvrirModalGabarit} /></>}>
                    <InputNumber min={1} max={120} placeholder="Ex: 1" disabled={!notifActive} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="NbSuspension" labelCol={{ span: 16 }} label={<>Suspension (<GabaritTag code="SFC03" onClick={ouvrirModalGabarit} /> à seuil − 1, <GabaritTag code="SFC04" onClick={ouvrirModalGabarit} />)</>}>
                    <InputNumber min={1} max={120} placeholder="Ex: 3" disabled={!notifActive} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="NbResiliation" labelCol={{ span: 16 }} label={<>Résiliation <GabaritTag code="SFC05" onClick={ouvrirModalGabarit} /></>}>
                    <InputNumber min={1} max={120} placeholder="Ex: 6" disabled={!notifActive} style={{ width: '100%' }} />
                  </Form.Item>
                </SectionCard>

                <SectionCard
                  numero={3}
                  titre="Relance des impayés et délai de régularisation"
                  caption={<>La relance renvoie le SMS d'avertissement <GabaritTag code="SFC02" onClick={ouvrirModalGabarit} /> tant que le client reste impayé en dessous du seuil de suspension. Le délai de régularisation suspend la sanction : quand le seuil suspension ou résiliation est atteint, le client reçoit un SMS « suspendu/résilié dans X jours » (<GabaritTag code="SFC14" onClick={ouvrirModalGabarit} /> / <GabaritTag code="SFC15" onClick={ouvrirModalGabarit} />) et le statut ne change qu'à la fin du délai. Vide = sanction immédiate.</>}
                >
                  <Form.Item
                    name="RelanceImpayeJours" labelCol={{ span: 16 }}
                    label="Relancer l'avertissement tous les (jours) — vide = jamais"
                  >
                    <InputNumber min={1} max={365} placeholder="Ex: 15" disabled={!notifActive} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item
                    name="DelaiRegularisationJours" labelCol={{ span: 16 }}
                    label="Délai de régularisation avant sanction (jours) — vide = immédiat"
                  >
                    <InputNumber min={0} max={365} placeholder="Ex: 30" disabled={!notifActive} style={{ width: '100%' }} />
                  </Form.Item>
                </SectionCard>

              </Box>

              {/* Colonne droite : 4 Événements */}
              <Box fontStyle="inherit" sx={{ flex: 1, minWidth: 380 }}>

                <SectionCard
                  numero={4}
                  titre={`Événements notifiés au client (${evtActifs.length})`}
                  caption="Ajoutez un événement via la picklist : il est activé immédiatement et disparaît des disponibles. Pour le retirer, basculez son interrupteur. Survolez un code gabarit pour voir son usage et son déclencheur ; cliquez dessus pour modifier son message."
                >
                  {/* Picklist (style SearchModal) : ne propose que les événements non activés */}
                  <Form.Item label="Ajouter un événement" labelCol={{ span: 8 }} style={{ marginBottom: 8 }}>
                    <Space.Compact block>
                      <Input.Search
                        placeholder="Rechercher..."
                        value={picklistEvtSearch}
                        onChange={(e) => setPicklistEvtSearch(e.target.value)}
                        onSearch={() => setPicklistEvtOpen(true)}
                        disabled={!notifActive || evtDispos.length === 0}
                        style={{ width: 167 }}
                      />
                      <Input
                        disabled
                        placeholder={evtDispos.length === 0 ? 'Tous les événements sont activés' : 'Sélectionnez un événement'}
                        style={{ width: 320 }}
                      />
                    </Space.Compact>
                  </Form.Item>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    {evtActifs.map((name) => {
                      const ev = EVENEMENTS.find((e) => e.name === name)!;
                      return (
                        <Box key={ev.name} sx={{
                          display: 'flex', alignItems: 'center', gap: 1.5,
                          py: 0.75, px: 1, borderRadius: 1.5,
                          background: 'var(--lightColor001)',
                          border: '1px solid var(--colorOpaque030)',
                          opacity: notifActive ? 1 : 0.45,
                        }}>
                          <Form.Item name={ev.name} valuePropName="checked" style={{ marginBottom: 0 }}>
                            <Switch
                              disabled={!notifActive} size="small"
                              onChange={(v) => { if (!v) retirerEvt(ev.name); }}
                            />
                          </Form.Item>
                          <Box sx={{ flex: 1, fontSize: 13 }}>{ev.label}</Box>
                          <GabaritTag code={ev.code} onClick={ouvrirModalGabarit} />
                        </Box>
                      );
                    })}
                    {evtActifs.length === 0 && (
                      <div style={{ color: '#8c8c8c', fontSize: 12, padding: '4px 2px' }}>
                        Aucun événement activé — utilisez la picklist ci-dessus pour en ajouter.
                      </div>
                    )}
                  </Space>

                  {/* Gabarits SMS : ajouter un code SFC ou modifier le message d'un existant */}
                  <Box sx={{
                    mt: 1.5, pt: 1.5, display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                    borderTop: '1px dashed var(--colorOpaque030)',
                  }}>
                    <Typography variant="caption" color="textSecondary" sx={{ flex: 1, minWidth: 180 }}>
                      Gabarits : cliquez sur un code SFCxx pour modifier son message.
                    </Typography>
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => ouvrirModalGabarit()}
                    >
                      Ajouter / modifier un gabarit
                    </Button>
                  </Box>
                </SectionCard>

              </Box>
            </Box>
          </Box>
        </div>
      </div>
    ),
  }];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>SAFE CARE - PARAMÈTRES DES NOTIFICATIONS SMS</b>
      </Typography>
      <Form autoComplete="off" labelAlign="left" form={form} style={{ minWidth: 'auto', maxWidth: 'auto' }}>
        {/* Barre d'actions (charte cSAL, identique à Configuration Produit) */}
        <div className="bG">
          <legend>
            <Typography color="#116599">Actions</Typography>
          </legend>
          <Form.Item shouldUpdate className="bouttong">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block loading={saving} onClick={enregistrer}>
                Enregistrer
              </Button>
              <Button size="large" className="bt_add" type="ghost" htmlType="button" block loading={isLoading} onClick={() => refetch()}>
                Actualiser
              </Button>
              <Button size="large" className="bt_add" type="ghost" htmlType="reset" block onClick={annuler}>
                Annuler
              </Button>
            </Space>
          </Form.Item>
        </div>
        <div className="clientP">
          <div>
            <Tabs size="small" type="card" defaultActiveKey="1" items={items} animated={{ inkBar: true, tabPane: true }} />
          </div>
        </div>
      </Form>

      {/* Picklist des événements disponibles — Dialog style SearchModal :
          un événement choisi est activé immédiatement et disparaît de la liste */}
      <Dialog
        className="modal"
        maxWidth="md"
        open={picklistEvtOpen}
        onClose={() => { setPicklistEvtOpen(false); setPicklistEvtSearch(''); }}
        sx={{ '& .MuiDialog-paper': { minWidth: 'sm', maxHeight: 'md' } }}
      >
        <DialogTitle>Sélectionnez l'événement à activer</DialogTitle>
        <DialogContent dividers></DialogContent>
        <Stack direction="row" spacing={5}>
          <Form name="picklistEvtForm" wrapperCol={{ span: 16 }} style={{ minWidth: 600, marginLeft: 50 }} initialValues={{ remember: true }}>
            <Tabs defaultActiveKey="3" items={[
              {
                key: '1',
                label: <span id="Tab"><Typography><UserOutlined />CODE</Typography></span>,
                children: <InputForm notRequired name="text" type="text" label="CODE" />,
              },
              {
                key: '2',
                label: <span id="Tab"><Typography><FileTextOutlined />LIBELLE</Typography></span>,
                children: <InputForm notRequired name="text" type="text" label="LIBELLE" />,
              },
              {
                key: '3',
                label: <span id="Tab"><Typography><ContainerFilled />ALL</Typography></span>,
                children: (
                  <Box fontStyle="inherit" display="flex">
                    <InputForm
                      noupercase
                      taille={600}
                      autoFocus
                      notRequired
                      name="text"
                      type="text"
                      label="TEXT"
                      onChange={(e: any) => setPicklistEvtSearch(e.target.value.trim())}
                    />
                    <hr />
                    <Button style={{ top: '10px' }}>Rechercher</Button>
                  </Box>
                ),
              },
            ]} />
          </Form>
        </Stack>
        <DialogContent dividers>
          <Table
            style={{ width: 'auto' }}
            size="small"
            rowSelection={{
              type: 'radio',
              onChange: (_keys: React.Key[], rows: any[]) => {
                if (rows[0]) {
                  activerEvt(rows[0].name);
                  setPicklistEvtOpen(false);
                  setPicklistEvtSearch('');
                }
              },
            }}
            scroll={{ y: 250, x: 10 }}
            rowKey="name"
            pagination={false}
            columns={[
              { title: 'Code', dataIndex: 'code', width: 80 },
              {
                title: 'Libelle',
                dataIndex: 'label',
                filteredValue: picklistEvtSearch ? [picklistEvtSearch] : [],
                onFilter: (value: any, record: any) =>
                  String(record.label).toLowerCase().includes(String(value).toLowerCase()) ||
                  String(record.code).toLowerCase().includes(String(value).toLowerCase()),
              },
            ]}
            dataSource={evtDispos}
          />
        </DialogContent>
      </Dialog>

      {/* Formulaire gabarit SMS : ajouter un nouveau code ou modifier le
          message d'un gabarit existant (table FormatSms via FormatSMSController).
          Le Select charge un gabarit existant ; « Nouveau » rebascule en ajout. */}
      <Dialog
        className="modal"
        maxWidth="sm"
        fullWidth
        open={gabaritModalOpen}
        onClose={() => setGabaritModalOpen(false)}
      >
        <DialogTitle>{gabaritSel ? `Modifier le gabarit ${gabaritSel.codSmS}` : 'Ajouter un gabarit SMS'}</DialogTitle>
        <DialogContent dividers>
          <Form form={gabaritForm} layout="vertical" autoComplete="off" style={{ minWidth: 400 }}>
            <Form.Item label="Gabarit existant à modifier" style={{ marginBottom: 12 }}>
              <Select
                showSearch
                allowClear
                placeholder="Choisir un gabarit — vide = création d'un nouveau code"
                value={gabaritSel?.codSmS ?? undefined}
                optionFilterProp="label"
                onChange={(code) => {
                  const g = gabaritsSfc.find((x) => x.codSmS === code);
                  if (g) chargerGabarit(g);
                }}
                onClear={nouveauGabarit}
                options={gabaritsSfc.map((g) => ({
                  value: g.codSmS,
                  label: `${g.codSmS} — ${g.descriptionSMS ?? ''}`,
                }))}
                style={{ width: '100%' }}
              />
            </Form.Item>
            <Form.Item
              name="codSmS" label="Code SMS"
              rules={[{ required: true, message: 'Le code SMS est obligatoire' }]}
            >
              <Input
                placeholder="Ex : SFC16"
                disabled={!!gabaritSel}
                style={{ textTransform: 'uppercase' }}
              />
            </Form.Item>
            <Form.Item
              name="descriptionSMS" label="Description"
              rules={[{ required: true, message: 'La description est obligatoire' }]}
            >
              <Input placeholder="Ex : Safe Care - confirmation de prélèvement" />
            </Form.Item>
            <Form.Item
              name="message" label="Message"
              rules={[{ required: true, message: 'Le message est obligatoire' }]}
            >
              <Input.TextArea
                rows={5}
                maxLength={480}
                showCount
                placeholder="Texte du SMS envoyé au client"
              />
            </Form.Item>
            <Form.Item
              name="isActif" valuePropName="checked" style={{ marginBottom: 8 }}
              label="Gabarit actif (un gabarit inactif n'est jamais envoyé)"
            >
              <Switch />
            </Form.Item>
            <div style={{ color: '#8c8c8c', fontSize: 12 }}>
              Variables disponibles : #CustomerNames# #ClientID# #AccountID# #Amount# #Date# #NbImpayes# #PubliciteID#
            </div>
          </Form>
        </DialogContent>
        <DialogContent style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={nouveauGabarit}>Nouveau</Button>
          <Button type="primary" loading={savingGabarit} onClick={enregistrerGabarit}>
            Enregistrer
          </Button>
          <Button onClick={() => setGabaritModalOpen(false)}>Fermer</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SendMessagePage;
